import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {GetAnalysisPipelineStep} from 'analysis-tool/AnalysisTypes';
import {assert} from 'Asserts';
import FileBrowserActions from 'file-browser/FileBrowserActions';
import {PipelineStepStatusCode} from 'graphql-client/__generated/graphqlGlobalTypes';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {getPipelineStepConfig} from 'pipelines/StepShapeUtils';
import {FONT_PROXIMA_11} from 'styles/FontConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import Button, {ButtonStyle} from 'ui-library/buttons/Button';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {readableTime} from 'util/DateUtil';

import DocumentName from './DocumentName';
import {getDocumentIdFromStep} from './OutputsUtil';

export interface SendToDocumentSidebarItemTitleProps {
  step: GetAnalysisPipelineStep;
}

export default function SendToDocumentSidebarItemTitle({
  step,
}: SendToDocumentSidebarItemTitleProps): JSX.Element {
  const stepConfig = getPipelineStepConfig(step, PipelineStepType.SEND_TO_DOCUMENT, 0);
  const numItems = stepConfig.sectionLayout.reduce(
    (total, section) => total + section.inputReferences.length,
    0
  );

  let subtitleText: string;

  switch (step.statusCode) {
    case PipelineStepStatusCode.WAITING:
    case PipelineStepStatusCode.RUNNING:
    case PipelineStepStatusCode.BLOCKED:
      subtitleText = 'Sending outputs...';
      break;

    case PipelineStepStatusCode.FAILED:
      subtitleText = step.statusMessage ?? 'Failed to send outputs';
      break;

    case PipelineStepStatusCode.SUCCEEDED:
      subtitleText = `${readableTime(step.modifiedAt)} | ${
        numItems > 1 ? `${numItems} items` : `${numItems} item`
      }`;
      break;

    default:
      assert(false, 'Unexpected status code for send to document step', {
        statusCode: step.statusCode,
        type: step.definition.stepType,
      });
  }

  return (
    <div className={css(styles.wrapper)}>
      <div>
        <DocumentName step={step} />
        <div
          className={css(styles.subtitleText)}
          {...getTestProps({component: 'SendToDocumentSidebarItemTitle', element: 'subtitle'})}
        >
          {subtitleText}
        </div>
      </div>
      <span>
        {step.statusCode === PipelineStepStatusCode.SUCCEEDED && (
          <Button
            buttonStyle={ButtonStyle.LINK}
            icon="open"
            onClick={() => {
              const documentId = getDocumentIdFromStep(step);
              FileBrowserActions.openFile(documentId);
            }}
          />
        )}
        {(step.statusCode === PipelineStepStatusCode.RUNNING ||
          step.statusCode === PipelineStepStatusCode.WAITING ||
          step.statusCode === PipelineStepStatusCode.BLOCKED) && (
          <BenchlingIcon className={css(styles.runningIcon)} icon="hourglass-half" />
        )}
        {step.statusCode === PipelineStepStatusCode.FAILED && (
          <BenchlingIcon className={css(styles.errorIcon)} icon="error" />
        )}
      </span>
    </div>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
  },
  runningIcon: {
    color: Color.BLUE_450,
  },
  errorIcon: {
    color: Color.RED_500,
  },
  subtitleText: {
    ...FONT_PROXIMA_11,
    color: Color.GRAY_600,
  },
});
